import { useState } from "react";
import { Box, Button, Menu, MenuItem, Stack, Typography, type TypographyProps } from "@mui/material";
import { Translate, useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import { COMPLIANCE_REVIEW_INTERVAL_MONTHS } from "../../api/partners";
import type { ComplianceWithAnswers } from "../../api/partners";
import { complianceHistoryQueryOptions, updateComplianceReviewIntervalMutationOptions } from "../../api/partners";
import { EvaluationDot } from "./ComplianceSubDrawer";
import dayjs from "dayjs";
import { AlertTriangleIcon } from "../../shared/icons";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { prefetch } from "../../api/prefetch";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";

export const ComplianceHeaderMeta = ({
  complianceData,
  isPartnerArchived,
}: {
  complianceData: ComplianceWithAnswers;
  isPartnerArchived?: boolean;
}) => {
  const { paths, navigate } = usePrivacyNavigate();
  const { t, formatDate } = useTranslation();
  const [reviewIntervalMenuAnchorEl, setReviewIntervalMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const currentOrganisation = useCurrentOrganisation();
  const hasPartnerSecurityAndPrivacyProfileEnabled = currentOrganisation.partnerSecurityAndPrivacyProfileEnabled;

  const updateReviewIntervalMutation = useMutation(
    updateComplianceReviewIntervalMutationOptions({ complianceId: complianceData.id })
  );

  const { evaluation, external, nextAudit, reviewIntervalMonths = 12, manual } = complianceData;

  return (
    <Stack sx={{ py: 1.3, minWidth: "max-content", justifyContent: "center", gap: 1, alignItems: "flex-start" }}>
      <Stack direction="row" sx={{ alignItems: "baseline", justifyContent: "space-between", gap: 2, width: "100%" }}>
        {evaluation.assessment && evaluation.reviewed && (
          <Stack direction="row" sx={{ gap: 0.5, alignItems: "baseline" }}>
            <Box sx={{ alignSelf: "center", display: "flex", pb: "2px" }}>
              <EvaluationDot evaluation={evaluation.assessment} />
            </Box>
            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600, lineHeight: 1 }}>
              {t(`Compliance.Subdrawer.Evaluation.${evaluation.assessment}`)}
            </Typography>
            <Text>{formatDate(evaluation.reviewed.at, { showTime: false })}</Text>
          </Stack>
        )}

        {manual?.answerUpdatedAt && (
          <Button
            size="small"
            variant="text"
            color="primary"
            sx={{
              "padding": 0,
              "minWidth": 0,
              "fontSize": theme => theme.typography.pxToRem(12),
              "lineHeight": 1,
              "&:before": {
                content: "''",
                position: "absolute",
                inset: 0,
                transform: "scale(2)",
                transformOrigin: "right center",
              },
            }}
            onMouseOver={() => prefetch(complianceHistoryQueryOptions(complianceData.id))}
            onClick={() =>
              navigate(
                paths.partnerComplianceManualHistory({
                  id: complianceData.partnerId,
                })
              )
            }
          >
            {t("Compliance.Subdrawer.HeaderMeta.History")}
          </Button>
        )}
      </Stack>
      <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
        {manual?.answerUpdatedAt && <Text>{t("Compliance.Subdrawer.HeaderMeta.ManualEntry")}</Text>}
        {external && (
          <Text>
            {t("Compliance.Subdrawer.HeaderMeta.AnsweredBy", {
              name: `${external.answeredBy.firstName} ${external.answeredBy.lastName}`,
            })}
          </Text>
        )}

        {evaluation.reviewed && (
          <>
            <Text>•</Text>
            <Text>
              {t("Compliance.Subdrawer.HeaderMeta.ReviewedBy", {
                name: `${evaluation.reviewed.by.firstName} ${evaluation.reviewed.by.lastName}`,
              })}
            </Text>
          </>
        )}
      </Stack>
      {(() => {
        if (!nextAudit) return null;

        const auditHasExpired = dayjs(nextAudit).isBefore(dayjs(), "day");

        if (auditHasExpired) {
          return (
            <Stack
              direction="row"
              sx={{ alignItems: "center", gap: 0.5, color: theme => theme.palette.brandOrange["500"] }}
            >
              <AlertTriangleIcon size={14} color="currentColor" />
              <HACK_alignIconWithLabelBox>
                <Text sx={{ color: "currentColor" }}>{t("Compliance.Subdrawer.HeaderMeta.ReviewOverdue")}</Text>
              </HACK_alignIconWithLabelBox>
            </Stack>
          );
        }

        return (
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <Text>
              {t("Compliance.Subdrawer.HeaderMeta.NextReview", { date: formatDate(nextAudit, { showTime: false }) })}
            </Text>
            <Text>•</Text>
            <Text>
              <Translate
                translationKey="Compliance.Subdrawer.HeaderMeta.ReviewIntervalWithChange"
                values={{
                  interval: (
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      disabled={!hasPartnerSecurityAndPrivacyProfileEnabled || isPartnerArchived}
                      sx={{
                        "padding": 0,
                        "minWidth": 0,
                        "fontSize": "inherit",
                        "fontWeight": "inherit",
                        "lineHeight": "inherit",
                        "verticalAlign": "baseline",
                        "&:before": { content: "''", position: "absolute", inset: 0, transform: "scaleY(3)" },
                        "&.Mui-disabled": { color: theme => theme.palette.text.tertiary },
                      }}
                      onClick={e => setReviewIntervalMenuAnchorEl(e.currentTarget)}
                    >
                      {t("Compliance.Subdrawer.HeaderMeta.ReviewIntervalShort", { months: reviewIntervalMonths })}
                    </Button>
                  ),
                }}
              />
            </Text>
          </Stack>
        );
      })()}
      <Menu
        anchorEl={reviewIntervalMenuAnchorEl}
        open={Boolean(reviewIntervalMenuAnchorEl)}
        onClose={() => setReviewIntervalMenuAnchorEl(null)}
      >
        {COMPLIANCE_REVIEW_INTERVAL_MONTHS.map(option => (
          <MenuItem
            key={option}
            disabled={updateReviewIntervalMutation.isPending}
            selected={option === reviewIntervalMonths}
            onClick={() => {
              setReviewIntervalMenuAnchorEl(null);
              updateReviewIntervalMutation.mutate({ reviewIntervalMonths: option });
            }}
          >
            {t("Compliance.Subdrawer.HeaderMeta.EveryMonths", { months: option })}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

const Text = (props: TypographyProps) => {
  return (
    <Typography
      sx={{
        fontSize: theme => theme.typography.pxToRem(12),
        lineHeight: 1,
        color: theme => theme.palette.text.tertiary,
        ...props.sx,
      }}
    >
      {props.children}
    </Typography>
  );
};
